import React from 'react'

export default function MonthlyAttendanceMatrixTable({
  daysMeta = [],
  students = [],
  loading = false,
  onStudentClick,
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full min-w-[1200px] text-left text-xs text-on-surface border-collapse">
          {/* ── Table Header ── */}
          <thead className="border-b border-outline-variant/25 bg-surface-container-low/80 text-[11px] font-black uppercase tracking-wider text-on-surface-variant select-none">
            <tr>
              {/* Roll No */}
              <th className="sticky left-0 z-20 bg-surface-container-low/95 px-3 py-3.5 text-center w-16 min-w-[64px] border-r border-outline-variant/20 shadow-xs">
                Roll No
              </th>

              {/* Student Name */}
              <th className="sticky left-16 z-20 bg-surface-container-low/95 px-4 py-3.5 min-w-[200px] border-r border-outline-variant/20 shadow-xs">
                Student Name / Batch
              </th>

              {/* Day Columns (1 to N) */}
              {daysMeta.map((d) => (
                <th
                  key={d.day}
                  className={`px-1.5 py-2.5 text-center min-w-[34px] w-[34px] border-r border-outline-variant/15 ${
                    d.isSunday ? 'bg-amber-500/10 text-amber-900 font-black' : ''
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <span className="text-[11px] font-extrabold">{d.day}</span>
                    <span className={`text-[9px] font-bold ${d.isSunday ? 'text-amber-800' : 'text-on-surface-variant/70'}`}>
                      {d.weekday}
                    </span>
                  </div>
                </th>
              ))}

              {/* Summary Columns */}
              <th className="px-3 py-3.5 text-center w-16 min-w-[60px] text-emerald-800 bg-emerald-500/5 font-black border-r border-outline-variant/20">
                P
              </th>
              <th className="px-3 py-3.5 text-center w-16 min-w-[60px] text-rose-800 bg-rose-500/5 font-black border-r border-outline-variant/20">
                A
              </th>
              <th className="px-3 py-3.5 text-center w-20 min-w-[75px] font-black">
                %
              </th>
            </tr>
          </thead>

          {/* ── Table Body ── */}
          <tbody className="divide-y divide-outline-variant/15 font-medium">
            {loading ? (
              <tr>
                <td
                  colSpan={daysMeta.length + 5}
                  className="py-20 text-center text-xs text-on-surface-variant"
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined animate-spin text-2xl text-primary">
                      progress_activity
                    </span>
                    <span>Loading monthly attendance matrix...</span>
                  </div>
                </td>
              </tr>
            ) : students.length === 0 ? (
              <tr>
                <td
                  colSpan={daysMeta.length + 5}
                  className="py-20 text-center text-xs text-on-surface-variant"
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-4xl text-outline-variant">
                      person_search
                    </span>
                    <p className="font-bold text-on-surface">No Students Found</p>
                    <p className="text-[11px]">Adjust your filter selection to view monthly records.</p>
                  </div>
                </td>
              </tr>
            ) : (
              students.map((student) => {
                const pct = student.percentage || 0
                const pctColor =
                  pct >= 75
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 font-extrabold'
                    : pct >= 60
                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 font-bold'
                    : 'border-rose-500/40 bg-rose-500/10 text-rose-700 font-black animate-pulse'

                return (
                  <tr
                    key={student.id}
                    className="group transition-colors hover:bg-surface-container-low/40"
                  >
                    {/* Sticky Roll No */}
                    <td className="sticky left-0 z-10 bg-surface-container-lowest group-hover:bg-surface-container-low/80 px-3 py-3 text-center border-r border-outline-variant/20 shadow-xs">
                      <span className="inline-flex items-center justify-center rounded-lg bg-surface-container-high px-2 py-0.5 text-xs font-black text-on-surface">
                        {student.rollNo}
                      </span>
                    </td>

                    {/* Sticky Student Name / Batch */}
                    <td className="sticky left-16 z-10 bg-surface-container-lowest group-hover:bg-surface-container-low/80 px-4 py-3 border-r border-outline-variant/20 shadow-xs">
                      <div className="flex flex-col min-w-0">
                        <button
                          type="button"
                          onClick={() => onStudentClick?.(student)}
                          className="font-display text-xs font-extrabold text-[#3b82f6] hover:underline text-left truncate cursor-pointer"
                        >
                          {student.fullName}
                        </button>
                        <span className="text-[10px] text-on-surface-variant truncate">
                          {student.batchNames}
                        </span>
                      </div>
                    </td>

                    {/* Day Matrix Cells (1 to N) */}
                    {daysMeta.map((d) => {
                      const status = student.dayAttendance?.[d.day] || '—'
                      const isSun = d.isSunday

                      let cellClass = 'text-on-surface-variant'
                      if (status === 'P') {
                        cellClass = 'text-emerald-700 bg-emerald-500/15 font-black rounded'
                      } else if (status === 'A') {
                        cellClass = 'text-rose-700 bg-rose-500/20 font-black rounded'
                      } else if (status === 'H') {
                        cellClass = 'text-indigo-700 bg-indigo-500/15 font-bold rounded'
                      }

                      return (
                        <td
                          key={d.day}
                          className={`px-0.5 py-2 text-center border-r border-outline-variant/15 ${
                            isSun ? 'bg-amber-500/5' : ''
                          }`}
                        >
                          <span className={`inline-block w-6 h-6 leading-6 text-[11px] ${cellClass}`}>
                            {status}
                          </span>
                        </td>
                      )
                    })}

                    {/* Summary: Present Days */}
                    <td className="px-2 py-3 text-center border-r border-outline-variant/20 bg-emerald-500/5">
                      <span className="font-extrabold text-emerald-800 text-xs">
                        {student.presentCount}
                      </span>
                    </td>

                    {/* Summary: Absent Days */}
                    <td className="px-2 py-3 text-center border-r border-outline-variant/20 bg-rose-500/5">
                      <span className="font-extrabold text-rose-800 text-xs">
                        {student.absentCount}
                      </span>
                    </td>

                    {/* Summary: Percentage */}
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center justify-center rounded-xl border px-2 py-0.5 text-xs ${pctColor}`}>
                        {pct}%
                      </span>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
